// Inspiration: https://github.com/nhagen/react-intercom/blob/master/src/index.js
/* eslint-disable new-cap */
import React from 'react';
import PropTypes from 'prop-types';
import {getConfig} from 'config';
import {getUserName} from 'utils/misc';
import {decodeNodeId} from 'utils/encoders';
import {withEmploymentAtOrganization} from 'api/organizations/employments/withEmploymentAtOrganization';
import {userShape, withAuthenticatedUser} from 'api/users/withAuthenticatedUser';
import {withRouter} from 'react-router-dom';
import isEqual from 'lodash.isequal';

const HIDE_INTERCOM_REGEXP = [
    /logic-test-irt\/.+\/test/
];


class IntercomLoader extends React.Component {
    static propTypes = {
        employment: PropTypes.shape({
            user: PropTypes.shape({
                id: PropTypes.string,
                firstName: PropTypes.string,
                lastName: PropTypes.string,
                email: PropTypes.string
            }),
            organization: PropTypes.shape({
                id: PropTypes.string,
                name: PropTypes.string
            })
        }),
        authenticatedUser: userShape,
        location: PropTypes.shape({
            pathname: PropTypes.string
        })
    };

    static defaultProps = {
        employment: {}
    };

    constructor(props) {
        super(props);
        this.config = getConfig();
        this.settings = null;
        this.booted = false;
        if (!window.Intercom) {
            (function(w, d, id, s, x) {
                function i() {
                    i.c(arguments);
                }

                i.q = [];
                i.c = function(args) {
                    i.q.push(args);
                };
                w.Intercom = i;
                s = d.createElement('script');
                s.async = 1;
                s.src = 'https://widget.intercom.io/widget/' + id;
                x = d.getElementsByTagName('script')[0];
                x.parentNode.insertBefore(s, x);
            })(window, window.document, this.config.INTERCOM_APP_ID);
        }
    }

    buildSettings = () => {
        const {authenticatedUser, employment} = this.props;
        const settings = {
            app_id: this.config.INTERCOM_APP_ID
        };

        if (authenticatedUser) {
            settings['user_id'] = decodeNodeId(authenticatedUser.id, true);
            settings['user_hash'] = authenticatedUser.intercomVerificationToken;
            settings['name'] = getUserName(authenticatedUser);
            settings['email'] = authenticatedUser.email;
        }

        // If the user is impersonating another user, DON'T add organization info.
        if (employment && authenticatedUser && employment.user && employment.user.id === authenticatedUser.id) {
            const {organization} = employment;
            // User is NOT impersonating
            // Add organization info
            if (organization) {
                settings['organization_id'] = decodeNodeId(organization.id, true);
                settings['organization'] = organization.name;
                settings['company'] = {
                    id: decodeNodeId(organization.id, true).toString(),
                    name: organization.name
                };
            }
        }
        return settings;
    };

    bootIntercom = () => {
        if (window.Intercom) {
            const settings = this.buildSettings();
            window.Intercom('boot', settings);
            this.settings = settings;
            this.booted = true;
        }
    };

    updateIntercom = () => {
        const settings = this.buildSettings();
        if (window.Intercom && !isEqual(settings, this.settings)) {
            window.Intercom('update', settings);
            this.settings = settings;
        }
    };

    shutdownIntercom = () => {
        if (window.Intercom) {
            window.Intercom('shutdown');
            this.settings = null;
            this.booted = false;
        }
    };

    shouldHideIntercom = () => {
        return HIDE_INTERCOM_REGEXP.some(regexp => !!this.props.location.pathname.match(regexp));
    };

    componentWillUnmount() {
        this.shutdownIntercom();
        delete window.Intercom;
    }

    componentDidMount() {
        if (!this.shouldHideIntercom()) {
            this.bootIntercom();
        }
    }

    componentDidUpdate() {
        if (this.booted && this.shouldHideIntercom()) {
            this.shutdownIntercom();
        }
        else if (!this.booted && !this.shouldHideIntercom()) {
            this.bootIntercom();
        }
        else if (this.booted) {
            this.updateIntercom();
        }
    }

    render() {
        return null;
    }
}

export default withRouter(withEmploymentAtOrganization(withAuthenticatedUser(IntercomLoader), true));


